import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Store, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { effStock } from "@/pages/Marketplace";

export default function Wishlist() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [listings, setListings] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!isAuthed) { openAuth("login"); setListings([]); return; }
    api.myWishlist().then((d) => setListings(d.listings || [])).catch(() => setListings([]));
  }, [isAuthed, openAuth]);

  const remove = async (id) => {
    setListings((p) => p.filter((l) => l.id !== id));
    try { await api.toggleWishlist(id); } catch { /* ignore */ }
  };

  const buy = async (l) => {
    if (l.variants?.length) return navigate(`/marketplace?listing=${l.id}`);
    setBusyId(l.id);
    try { const res = await api.marketplaceCheckout({ listing_id: l.id, origin_url: window.location.origin }); window.location.href = res.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="wishlist-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/marketplace")} className="flex w-fit items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="wishlist-back"><ArrowLeft size={15} /> Marketplace</button>
        <span className="hr-overline text-saddle">Saved for later</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-forest mt-2 flex items-center gap-3"><Heart className="fill-blaze text-blaze" size={34} /> My Wishlist</h1>

        {listings === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : listings.length === 0 ? (
          <div className="mt-8 border border-dashed border-saddle/30 p-10 text-center" data-testid="wishlist-empty">
            <Heart size={30} className="mx-auto text-saddle/40" />
            <p className="font-serif text-2xl text-forest mt-3">Nothing saved yet.</p>
            <p className="text-charcoal/60 text-sm mt-1">Tap the heart on any product to save it here for later.</p>
            <button onClick={() => navigate("/marketplace")} className="mt-5 bg-forest text-bone px-5 py-2.5 text-sm font-semibold hover:bg-forest-2 transition-colors" data-testid="wishlist-shop">Browse the marketplace</button>
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wishlist-grid">
            {listings.map((l) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-saddle/20 flex flex-col overflow-hidden" data-testid="wishlist-card">
                <div className="h-44 bg-bone/60 overflow-hidden relative">
                  {l.images?.[0] ? <img src={l.images[0]} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-saddle/40"><Store size={26} /></div>}
                  <button onClick={() => remove(l.id)} className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5" aria-label="Remove from wishlist" data-testid="wishlist-remove"><Heart size={15} className="fill-blaze text-blaze" /></button>
                  {effStock(l) != null && effStock(l) <= 0 && <span className="absolute bottom-2 left-2 text-[9px] font-bold uppercase bg-charcoal text-white px-1.5 py-0.5">Sold out</span>}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-serif text-lg text-forest">{l.title}</h3>
                  <button onClick={() => navigate(`/store/${l.business_id}`)} className="text-xs text-saddle hover:text-blaze mt-0.5 text-left">{l.business_name} →</button>
                  {l.shipping_cost != null && <span className="text-[11px] text-saddle mt-1 inline-flex items-center gap-1"><Truck size={11} /> {l.shipping_cost > 0 ? `+$${l.shipping_cost} ship` : "Free ship"}</span>}
                  <div className="mt-auto pt-3 flex items-center justify-between">
                    <span className="font-serif text-2xl text-forest tabular-nums">${l.price?.toLocaleString()}</span>
                    {effStock(l) != null && effStock(l) <= 0 ? (
                      <span className="text-xs font-semibold text-charcoal/40">Sold out</span>
                    ) : (
                      <button onClick={() => buy(l)} disabled={busyId === l.id} className="bg-forest text-bone px-4 py-2 text-sm font-semibold hover:bg-forest-2 transition-colors disabled:opacity-60" data-testid="wishlist-buy">{busyId === l.id ? <Loader2 size={14} className="animate-spin" /> : (l.variants?.length ? "Options" : "Buy")}</button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
